import { ensureDefined } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { DocumentEditorActionType } from '../../enums/action-type.enum';
import { DocumentEditorTwoStepContentActionType } from '../../enums/two-step-content-action-type.enum';
import { DocumentEditorInstantaneousContentActionType } from '../../enums/instantaneous-content-action-type.enum';
import type { DocumentEditorState } from '../../types/editor-state.type';
import type { Editable } from '../../types/editable.type';
import type { DocumentEditorAction } from '../../types/actions.type';
import type { DocumentEditorContentAction } from '../../types/content-actions.type';

import type { ReducerContext } from '../types/reducer-context.type';

export const historyReducer = <E extends Editable>(
  state: DocumentEditorState<E>,
  action: DocumentEditorAction<E> | DocumentEditorContentAction,
  context: Readonly<ReducerContext<E>>,
): DocumentEditorState<E> | null => {
  switch (action.type) {
    case DocumentEditorActionType.Undo: {
      if (!state.pastVersions.length) {
        return state;
      }

      const pastVersions = [...state.pastVersions];

      const { version } = ensureDefined(
        { version: pastVersions.pop() },
        'Version not found',
      );

      return {
        ...state,
        pastVersions,
        futureVersions: [
          ...state.futureVersions,
          {
            editable: state.editable,
            currentPath: version.currentPath,
            currentVersion: state.currentVersion,
          },
        ],
        ...version,
        navigationTarget: version.currentPath,
        currentVersion: version.currentVersion,
      };
    }

    case DocumentEditorActionType.Redo: {
      if (!state.futureVersions.length) {
        return state;
      }

      const futureVersions = [...state.futureVersions];

      const { version } = ensureDefined(
        { version: futureVersions.pop() },
        'Version not found',
      );

      return {
        ...state,
        pastVersions: context.pushInPast(state),
        futureVersions,
        ...version,
        navigationTarget: version.currentPath,
        currentVersion: version.currentVersion,
      };
    }

    case DocumentEditorActionType.ClearHistory: {
      return {
        ...state,
        editable: context.initialEditable,
        pastVersions: EMPTY_ARRAY,
        futureVersions: EMPTY_ARRAY,
        currentVersion: 0,
        latestSavedVersion: undefined,
      };
    }

    case DocumentEditorActionType.BumpLatestSavedVersion: {
      return {
        ...state,
        latestSavedVersion: state.currentVersion,
      };
    }

    case DocumentEditorActionType.ClearNavigationTarget: {
      const { navigationTarget: _navigationTarget, ...newState } = state;

      return newState;
    }

    case DocumentEditorTwoStepContentActionType.InsertBefore:
    case DocumentEditorTwoStepContentActionType.InsertAfter:
    case DocumentEditorTwoStepContentActionType.InsertInside:
    case DocumentEditorTwoStepContentActionType.InsertStart:
    case DocumentEditorTwoStepContentActionType.InsertEnd:
    case DocumentEditorTwoStepContentActionType.Update:
    case DocumentEditorActionType.OpenDrawer:
    case DocumentEditorActionType.CloseDrawer:
    case DocumentEditorActionType.FoldAllSections:
    case DocumentEditorActionType.UnfoldAllSections:
    case DocumentEditorActionType.SetEditorMode:
    case DocumentEditorActionType.RequestTwoStepContentAction:
    case DocumentEditorActionType.ClearTwoStepContentAction:
    case DocumentEditorActionType.UpdateInfo:
    case DocumentEditorActionType.UpdateAdmin:
    case DocumentEditorInstantaneousContentActionType.Duplicate:
    case DocumentEditorInstantaneousContentActionType.Move:
    case DocumentEditorInstantaneousContentActionType.SetFoldingMode:
    case DocumentEditorInstantaneousContentActionType.Delete:
    case DocumentEditorInstantaneousContentActionType.Reinitialize:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakBefore:
    case DocumentEditorInstantaneousContentActionType.SetCustomPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetPageBreakAfter:
    case DocumentEditorInstantaneousContentActionType.SetOrientation:
    case DocumentEditorInstantaneousContentActionType.SetPaperFormat:
    case DocumentEditorInstantaneousContentActionType.SetDisplayCondition:
    default: {
      return null; // Not handled by this reducer
    }
  }
};
